import {UploadFile} from "antd";
import {UploadChangeParam} from "antd/lib/upload/interface";
import _ from "lodash";
import {makeAutoObservable} from "mobx";
import Axios, {IAxiosConfig} from "../core/Axios";

enum Steps {
    new = 1,
    passport_selfie,
    passport_front = 3,
    passport_reg = 3,
    passport_text = 5,
    juridical_inn,
    doc_sign,
    done
    // moderation,
}

export interface IRecord {
    passport_selfie: UploadChangeParam<UploadFile>;
    passport_front: UploadChangeParam<UploadFile>;
    passport_reg: UploadChangeParam<UploadFile>;
}

class Verification {
    [Symbol.toStringTag] = this.constructor.name;
    private _timeout!: NodeJS.Timeout;
    private _step: number = Steps.new;
    private _loading: boolean = false;
    private _progress: number = 0;
    private _removedIndex: number = -1;
    private _record!: IRecord;

    constructor() {
        makeAutoObservable(this);
    }

    get step() {
        return this._step;
    }

    set step(value: number) {
        this._step = value;
    }

    get removedIndex() {
        return this._removedIndex;
    }

    get progress() {
        return this._progress;
    }

    get loading() {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
    }

    get record() {
        return this._record;
    }

    private _clearProgress = () => {
        clearTimeout(this._timeout);
        this._timeout = setTimeout(() => {
            this._progress = 0;
        }, 1000);
    };

    private _stopProgress = () => {
        clearTimeout(this._timeout);
        this._progress = 100;
        this.loading = false;
        this._clearProgress();
    };

    private _startProgress = async () => {
        this.loading = true;
        let i = 0;

        do {
            ++i;
            this._progress += 2;
        } while (i == i + 1);

        if (i && this._progress <= 100) {
            this._timeout = setTimeout(this._startProgress, 100);
        }
    };

    getPhoto(key: string) {
        const value = _.get(this._record, `${key}`);
        return value ? [value.file] : [];
    }

    async setPhoto(value: Pick<IRecord, "passport_selfie" | "passport_front" | "passport_reg">) {
        const param = _.first(_.values(value));

        if (_.size(param?.fileList)) {
            const code = _.first(_.keys(value));

            try {
                clearTimeout(this._timeout);
                this._startProgress();

                await Axios({
                    url: "/users/docs",
                    method: "POST",
                    loading: false,
                    data: {
                        code,
                        file: param?.file.originFileObj
                    }
                } as IAxiosConfig);

                _.set(value, `${code}.file.status`, "done");
            } catch (e: any) {
                if (_.isEqual(e?.response?.status, 422)) {
                    _.set(value, `${code}.file.status`, "done");
                } else {
                    _.set(value, `${code}.file.status`, "error");
                }
                throw e;
            } finally {
                if (_.lt(this.step, 4)) {
                    this.next();
                }
                this._record = {...this.record, ...value};
                this._stopProgress();
            }
        }
    }

    removePhoto(key: keyof IRecord) {
        delete this._record[key];

        setTimeout(() => {
            switch (key) {
                case "passport_reg":
                case "passport_front":
                    this.step = 3;
                    break;
                default:
                    this.step = 1;
                    break;
            }
        });
    }

    isAllPhotos() {
        return (
            _.size(this._record.passport_front) &&
            _.size(this._record.passport_reg) &&
            _.size(this._record.passport_selfie)
        );
    }

    next() {
        this.step += 1;
    }

    async getUsers() {
        try {
            await Axios({
                url: "/users",
                method: "GET",
                params: {
                    expand: "personal_data,docs"
                }
            });
            this.next();
        } catch (e: any) {
        } finally {
        }
    }
}

export default Verification;
